import { Controller } from "@hotwired/stimulus"

// The checkout half of the cart page (Samuel's demo, one page since 2026-10-16): delivery country and
// options, the address (our fields + the address-form Google autocomplete), the payment with Stripe
// Elements in deferred mode (no PaymentIntent until « Payer »). The server creates the intent at the
// click (POST paymentUrl) after re-checking the cart, the address and the total the buyer saw; this
// controller then confirms it with Stripe.js, which redirects to the confirmation page.
// Cart actions re-render the cart block and the recap (Turbo Streams): their targets reconnect and
// are repainted here. No optional chaining: the webpack/Babel setup does not transpile it.
// A subscription line (card 12, #secPaiement's data-subscription-mode) saves the card for the
// subscription's own off-session charge: card and Link only, setupFutureUsage on a mixed cart, and
// Elements in setup mode (confirmSetup, nothing paid here) for a cart of subscriptions only. Its
// first charge counts in the total shown and on « Payer » (Samuel's mock-up: one total), never in
// the intent's amount nor in the total the server re-checks (data-subscriptions-cents).
// The buyer's typed checkout, per tab (writeDraft).
const DRAFT_KEY = "shop-checkout-draft-"
// « Payer » found the cart edited elsewhere and the page loads again: said once after the reload.
const CART_CHANGED_KEY = "shop-checkout-cart-changed-"
// The layout with the sticky « Payer » bar (shop_cart.css): a phone, whose keyboard a focus opens.
const PHONE_LAYOUT = "(max-width: 860px)"

export default class extends Controller {
  static values = {
    publishableKey: String,
    minChargeCents: Number,
    paymentMethodTypes: Array,
    pickupAddress: Object,
    prefill: Object,
    noShipmentOption: String,
    shippingUrl: String,
    paymentUrl: String,
    lepotPaymentUrl: String,
    lepotEmailError: String,
    // The cart the draft belongs to: the next cart in the same tab starts empty.
    draftKey: String,
    klarnaMinCents: Number,
    klarnaCountry: String,
    klarnaEligible: String,
    klarnaTeaser: String,
    payLabel: String,
    freeLabel: String,
    pickupLabel: String,
    genericError: String,
    throttledError: String,
    missingError: String,
    requiredError: String,
    emailInvalidError: String
  }

  static targets = [
    "cart", "nudge", "delivery", "shipCountry", "shippingFrame",
    "deliveryTitle", "deliverySub", "address", "addressTitle", "addressSub", "giftToggle", "giftLine", "addressForm", "choice",
    "firstName", "lastName", "street", "street2", "postcode", "city", "phoneLabel", "phone",
    "paymentSection", "payWait", "payWaitText", "email", "express", "expressOr", "payment", "billingAddress", "errors", "lepot",
    "klarnaHint", "klarnaPill", "shippingLabel", "shippingCountry", "shippingAmount", "total", "submit", "mobileCta",
    "cartChanged"
  ]

  connect() {
    if (!this.hasCartTarget) return

    this.cartKey = this.cartKeyOf(this.cartTarget)
    this.subscriptionMode = this.hasPaymentSectionTarget ? (this.paymentSectionTarget.dataset.subscriptionMode || null) : null
    this.setup = this.subscriptionMode === "setup"
    this.kind = this.setup ? "subscriptions" : this.cartTarget.dataset.kind
    this.subtotalCents = this.cartSubtotal()
    this.subscriptionCents = this.cartSubscriptionCents()
    this.shippingCents = null
    this.optionKey = null
    this.optionLabel = null
    this.pickup = false
    this.paying = false
    this.gift = false
    // Back from a refused payment or from Le Pot: what the buyer typed (Shop::Confirmation#prefill,
    // Shop::Checkout#lepot_prefill). Not on a Back to a copy of this page: its prefill is older than
    // what the browser restored over it (a correction made since would be mixed with it). The option
    // is only a preference for the first quote; optionKey stays null until the frame offers it.
    this.prefill = this.navigationType() === "back_forward" ? {} : this.prefillValue
    this.preferredOptionKey = this.prefill.option_key || null
    // On a Back the browser restores the choice field only after connect: nothing is written to it
    // before followRestoredFields has read it.
    this.choice = {}
    this.choiceRead = this.navigationType() !== "back_forward"
    this.started = true
    this.showCartChanged()
    if (this.kind === "empty") return this.paintRecap()

    this.setUpStripe()
    // A Back keeps what the browser restored (master's rule): neither the prefill nor the draft.
    // A prefill of the account's e-mail alone (a signed-in buyer) does not hide the draft, whose
    // own e-mail wins: what the buyer typed before comes first.
    const draft = this.choiceRead ? this.readDraft() : {}
    const accountEmailOnly = Object.keys(this.prefill).every((key) => key === "email")
    const merged = Object.assign({}, this.prefill, draft)
    // A draft saved before any e-mail was typed (a guest who then signed in) keeps the account's.
    if (!draft.email) merged.email = this.prefill.email
    this.applyPrefill(accountEmailOnly ? merged : this.prefill)
    this.saveDraft = () => this.writeDraft()
    this.element.addEventListener("input", this.saveDraft)
    this.element.addEventListener("change", this.saveDraft)
    if (this.kind === "gift_cards" || this.setup) {
      // Nothing to quote: each card's delivery is in its price, the one option is « Sans envoi »
      // (a subscription is delivered to the address of its own line).
      this.optionKey = this.noShipmentOptionValue
      this.shippingCents = 0
    } else {
      this.reveal(this.deliveryTarget, true)
      this.reveal(this.addressTarget, true)
      this.loadShipping()
    }
    this.updateTotal()

    // Google's autocomplete turns the street's autocomplete off, and the browser then keeps no
    // value of it for a Back (from Stripe's or Le Pot's page, or any other): turned on again when
    // the page goes, so a Back gives the whole address back (card F step 5).
    this.leaving = () => {
      if (!this.hasStreetTarget || this.streetTarget.getAttribute("autocomplete") !== "off") return
      this.streetTarget.setAttribute("autocomplete", "address-line1")
      this.streetAutocompleteOff = true
    }
    window.addEventListener("pagehide", this.leaving)
    // Back from Stripe's (or Le Pot's) page restores this one whole from the back-forward cache,
    // « paying » set.
    this.restored = (event) => {
      if (!event.persisted) {
        if (this.navigationType() === "back_forward") this.followRestoredFields()
        return
      }
      if (this.streetAutocompleteOff) this.streetTarget.setAttribute("autocomplete", "off")
      this.streetAutocompleteOff = false
      this.fail("")
    }
    window.addEventListener("pageshow", this.restored)
  }

  // A Back to a copy of this page: the browser restores the fields after connect (only by
  // pageshow), so the quote, Stripe's methods and Google's country were set up for the empty
  // form. They follow the country and postcode the buyer had, as if typed again, and the option
  // and « C'est un cadeau » come back from the choice field.
  followRestoredFields() {
    if (this.kind === "empty") return
    const restored = this.readChoice()
    // Kept for the next Back even if the buyer leaves before the new quote checks the option.
    Object.assign(this.choice, restored)
    this.choiceRead = true
    if (restored.gift) this.setGift(true)
    const country = this.country()
    if (country && this.hasAddressFormTarget) this.addressFormTarget.setAttribute("data-address-form-locked-country-code-value", country)
    if (this.elements) this.elements.update({ paymentMethodTypes: this.methodTypes() })
    if (this.kind === "parcel" && restored.option && restored.option !== this.optionKey) {
      // Asked for again: the quote keeps it checked when the restored country still offers it.
      this.optionKey = null
      this.preferredOptionKey = restored.option
      this.quotedFor = null
    }
    if (this.kind === "parcel" && `${country}|${this.postcode()}` !== this.quotedFor) this.loadShipping()
    this.rememberChoice({})
    this.refreshPayability()
  }

  readChoice() {
    if (!this.hasChoiceTarget || !this.choiceTarget.value) return {}
    try {
      return JSON.parse(this.choiceTarget.value) || {}
    } catch (err) {
      return {}
    }
  }

  // What the browser does not restore itself (the option comes later, in a frame; « C'est un
  // cadeau » is a button), kept in a field it does restore.
  rememberChoice(changes) {
    Object.assign(this.choice, changes)
    if (this.choiceRead && this.hasChoiceTarget) this.choiceTarget.value = JSON.stringify(this.choice)
  }

  navigationType() {
    const entries = performance.getEntriesByType ? performance.getEntriesByType("navigation") : []
    return entries[0] ? entries[0].type : null
  }

  disconnect() {
    this.destroyKlarnaAddress()
    if (this.hasBillingAddressTarget) this.billingAddressTarget.hidden = true
    clearTimeout(this.quoteTimer)
    if (this.saveDraft) {
      this.element.removeEventListener("input", this.saveDraft)
      this.element.removeEventListener("change", this.saveDraft)
    }
    if (this.restored) window.removeEventListener("pageshow", this.restored)
    if (this.leaving) window.removeEventListener("pagehide", this.leaving)
  }

  // ---- Stripe ---------------------------------------------------------------------------------

  setUpStripe() {
    // Stripe.js blocked (an ad blocker, a network failure): the cart above still works; « Payer »
    // then fails with the generic error instead of the page breaking.
    if (typeof Stripe === "undefined") return
    this.stripe = Stripe(this.publishableKeyValue)
    const options = {
      mode: this.setup ? "setup" : "payment",
      currency: "eur",
      paymentMethodTypes: this.methodTypes(),
      locale: "fr",
      // 16px at least: a smaller input makes iOS Safari zoom on focus.
      appearance: { theme: "flat", variables: { fontFamily: "'DM Sans', sans-serif", fontSizeBase: "16px", borderRadius: "10px", colorText: "#281A39" } }
    }
    // Must match the intent the server creates (Shop::Checkout#create_intent / #start_setup!).
    if (!this.setup) options.amount = this.totalCents()
    if (this.subscriptionMode === "payment") options.setupFutureUsage = "off_session"
    this.elements = this.stripe.elements(options)

    // A delivery sends the shipping address as the billing one at confirmation (Klarna needs the
    // full address; left to the Payment Element its country picker defaulted to AT). Gift cards only
    // ask no address (demo): the Payment Element asks what the card needs, and Klarna's address
    // comes from syncKlarnaAddress.
    // Its country picker starts on the delivery country (a subscription line's when the cart has
    // no parcel), never Stripe's guess from the IP.
    const lineCountry = this.hasPaymentSectionTarget ? this.paymentSectionTarget.dataset.billingCountry : ""
    const payment = this.elements.create("payment", {
      terms: { card: "never" },
      fields: { billingDetails: { address: this.kind === "parcel" ? "never" : "auto", email: "never" } },
      defaultValues: { billingDetails: { address: { country: this.country() || lineCountry || "FR" } } }
    })
    payment.mount(this.paymentTarget)
    this.shownPaymentType = null
    payment.on("change", (event) => this.paymentChanged(event))

    // Klarna stays in the Payment Element: the Express Checkout Element would otherwise take it.
    // emailRequired: a guest paying with a wallet without typing the e-mail still gives one
    // (settlement mails the address on the charge).
    this.express = this.elements.create("expressCheckout", {
      emailRequired: true,
      paymentMethods: { applePay: "auto", googlePay: "auto", link: "auto", klarna: "never", paypal: "never", amazonPay: "never" }
    })
    this.express.mount(this.expressTarget)
    this.express.on("ready", (event) => {
      this.expressAvailable = !!(event.availablePaymentMethods && Object.values(event.availablePaymentMethods).some(Boolean))
      this.refreshPayability()
    })
    this.express.on("confirm", (event) => this.pay(event))
  }

  // Klarna needs the buyer's full billing address: with the country alone (the Payment Element's
  // « auto » asks nothing more) Klarna opens on the US market and refuses (« not available for
  // this region », test mode 2026-10-24). A delivery sends its own address; a pickup (the shop's
  // address is only a card's) and gift cards only ask it in an Address Element in billing mode,
  // shown only while Klarna is picked (Samuel 2026-10-24). Mounted, it is checked by
  // elements.submit() and attached at confirmation: it is destroyed for any other method, and while
  // a wallet (Apple Pay, Google Pay, Link above) pays, which brings its own billing details.
  // Never throws: fail() and pay() call it, and a Stripe.js error here must not leave the page
  // locked mid-payment. A block Stripe could not build stays hidden and unset, so elements.submit()
  // and the confirmation never count on a billing address that is not there.
  syncKlarnaAddress() {
    if (!this.elements || !this.hasBillingAddressTarget) return
    const wanted = this.shownPaymentType === "klarna" && (this.pickup || this.kind === "gift_cards") && !this.expressEvent
    this.billingAddressTarget.hidden = !wanted
    if (wanted === !!this.billingAddressElement) return
    if (!wanted) return this.destroyKlarnaAddress()

    try {
      // France only (Samuel 2026-10-24: where Klarna gives the 3×; Belgium has no Pay in 3): another
      // country would be refused only on Klarna's own page, after « Payer ».
      this.billingAddressElement = this.elements.create("address", { mode: "billing", allowedCountries: ["FR"], defaultValues: { address: { country: "FR" } } })
      this.billingAddressElement.mount(this.billingAddressTarget)
      this.billingAddressElement.on("change", (event) => { if (!event.empty) this.clearError() })
    } catch (err) {
      console.error("Klarna billing address unavailable", err)
      this.destroyKlarnaAddress()
      this.billingAddressTarget.hidden = true
    }
  }

  // Klarna picked where the page must ask its billing address, but Stripe could not build the
  // block: never confirm it with the pickup's or no address (the invoice names the payer from it).
  klarnaAddressMissing() {
    return this.shownPaymentType === "klarna" && (this.pickup || this.kind === "gift_cards") && !this.expressEvent && !this.billingAddressElement
  }

  destroyKlarnaAddress() {
    const element = this.billingAddressElement
    this.billingAddressElement = null
    if (!element) return
    try {
      element.destroy()
    } catch (err) {
      console.error("Klarna billing address not destroyed", err)
    }
  }

  // Klarna on every cart without a subscription line (Samuel 2026-10-24), from 100 € (card F);
  // with one, only what is saved for its charge. Bancontact on a delivery only for Belgium (Samuel's
  // mock-up, 2026-10-21), never on a pickup (quoted at the shop, in France); gift cards only keep it
  // (no country asked, Samuel 2026-09-30), and a subscription has neither. As
  // Shop::Checkout.payment_method_types_for.
  methodTypes() {
    if (this.subscriptionMode) return JSON.parse(this.paymentSectionTarget.dataset.subscriptionPaymentMethodTypes)
    const bancontact = this.kind !== "parcel" || (!this.pickup && this.country() === "BE")
    return this.paymentMethodTypesValue.filter((type) => {
      if (type === "bancontact") return bancontact
      if (type === "klarna") return this.klarnaOffered() && this.totalCents() >= this.klarnaMinCentsValue
      return true
    })
  }

  // Where Klarna's 3× is offered at all (from 100 €), without a subscription: a delivery to France
  // (card F), a pickup and a cart of gift cards only (their billing address in syncKlarnaAddress).
  klarnaOffered() {
    if (this.subscriptionMode) return false
    if (this.kind === "gift_cards") return true
    return this.kind === "parcel" && (this.pickup || this.country() === this.klarnaCountryValue)
  }

  // The demo's line: « Payable en 3× » from 100 €, below it what is still missing; nothing where
  // Klarna is not offered. And the Klarna pill among the payment logos.
  paintKlarna() {
    const offered = this.klarnaOffered()
    const missing = this.klarnaMinCentsValue - this.totalCents()
    const html = missing > 0 ? this.klarnaTeaserValue.replace("%{missing}", this.formatCents(missing)) : this.klarnaEligibleValue
    this.klarnaHintTargets.forEach((hint) => {
      hint.hidden = !offered
      // One span, as the demo's #klarnaTxt: the hint is a flex row.
      if (offered) hint.innerHTML = `<span>${html}</span>`
    })
    if (this.hasKlarnaPillTarget) this.klarnaPillTarget.hidden = !offered
  }

  // ---- Cart block and recap (re-rendered by cart actions) --------------------------------------

  cartSubtotal() {
    return parseInt(this.cartTarget.dataset.payableSubtotalCents, 10) || 0
  }

  cartSubscriptionCents() {
    return parseInt(this.cartTarget.dataset.subscriptionsCents, 10) || 0
  }

  // What decides the page's sections and Elements: the kind of cart and whether it holds a
  // subscription line (data-subscriptions on the cart block, rendered by the cart).
  cartKeyOf(element) {
    return `${element.dataset.kind}|${element.dataset.subscriptions || ""}`
  }

  // A cart action came back. Another kind of cart (emptied, a coffret added to gift cards, a
  // subscription added or removed...) needs other sections: the page reloads. Otherwise the total, Stripe's amount and the shipping options
  // (free shipping, a pickup not offered on a 0 € order) follow the new subtotal.
  cartTargetConnected(element) {
    if (!this.started) return
    if (this.cartKeyOf(element) !== this.cartKey) return window.location.reload()
    // The buyer has seen the new cart and acts on it: the « modifié ailleurs » notice has served.
    if (this.hasCartChangedTarget) this.cartChangedTarget.hidden = true
    this.paintDeliveryScope()
    this.paintAddressSub()
    // A stream landing while « Payer » runs (a gift note saved on blur): the click keeps the total
    // it read first, and the cart's numbers are taken once the payment fails (fail()). The new block
    // comes from the server without `inert`: put it back.
    if (this.paying) {
      this.cartChangedWhilePaying = true
      this.refreshPayability()
      return
    }
    this.syncCart()
  }

  syncCart() {
    // A duo's card comes back priced for its stored country: only a re-quote of the chosen one
    // (repriceDuos) puts the page's prices back.
    const duo = !!this.cartTarget.querySelector("[data-duo-line]")
    const subtotal = this.cartSubtotal()
    const subscriptions = this.cartSubscriptionCents()
    if (subtotal === this.subtotalCents && subscriptions === this.subscriptionCents && !duo) return
    this.subtotalCents = subtotal
    this.subscriptionCents = subscriptions
    this.updateTotal()
    this.reloadShipping()
  }

  totalTargetConnected() { if (this.started) this.paintRecap() }
  // The phone's line comes back with the cart block, hidden until painted.
  klarnaHintTargetConnected() { if (this.started) this.paintKlarna() }
  nudgeTargetConnected() { if (this.started) this.paintNudge() }

  paintRecap() {
    const total = this.displayTotalCents()
    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatCents(total)
    const label = this.kind === "empty" ? null : `${this.payLabelValue} ${this.formatCents(total)}`
    if (label) {
      if (this.hasSubmitTarget) this.submitTarget.textContent = label
      if (this.hasMobileCtaTarget) this.mobileCtaTarget.textContent = label
    }
    if (this.hasShippingAmountTarget) {
      const amount = this.shippingAmountTarget
      amount.classList.toggle("free", this.shippingCents === 0)
      amount.textContent = this.shippingCents === null ? "—" : (this.shippingCents === 0 ? (this.pickup ? this.pickupLabelValue : this.freeLabelValue) : this.formatCents(this.shippingCents))
    }
    if (this.hasShippingLabelTarget && this.optionLabel && this.pickup) this.shippingLabelTarget.textContent = this.optionLabel
    if (this.hasShippingCountryTarget) {
      this.shippingCountryTarget.textContent = this.pickup || !this.shippingCents ? "" : ` · ${this.countryName()}`
    }
    this.paintNudge()
    this.paintKlarna()
    this.refreshPayability()
  }

  // « Livraison offerte · appliquée » once the chosen delivery is the free one (demo's renderNudge).
  paintNudge() {
    if (!this.hasNudgeTarget) return
    const applied = this.shippingCents === 0 && !this.pickup && this.optionKey !== null
    this.nudgeTarget.classList.toggle("done", applied)
    this.nudgeTarget.querySelector(".nudge-pending").hidden = applied
    this.nudgeTarget.querySelector(".nudge-applied").hidden = !applied
  }

  // ---- Delivery ---------------------------------------------------------------------------------

  // « Pays de livraison » is the address's country too (the address block has none, Samuel
  // 2026-10-22): it prices the delivery and pins the Google autocomplete.
  countryChanged(event) {
    const code = event.target.value
    if (this.hasAddressFormTarget) this.addressFormTarget.setAttribute("data-address-form-locked-country-code-value", code)
    this.clearError()
    if (this.elements) this.elements.update({ paymentMethodTypes: this.methodTypes() })
    this.paintKlarna()
    this.loadShipping()
  }

  country() {
    return this.hasShipCountryTarget ? this.shipCountryTarget.value : ""
  }

  countryName() {
    if (!this.hasShipCountryTarget) return ""
    const option = this.shipCountryTarget.selectedOptions[0]
    return option ? option.textContent.replace(/^\S+\s+/, "").trim() : ""
  }

  // Quoted by country and, once typed, postcode (Corsica, overseas under FR); without a postcode the
  // server quotes a preview by country. A pickup too: every French address is offered it, and quoted
  // at the shop's, a Corsican buyer going back to a delivery would see mainland rates the payment
  // then refuses. `selected` is the buyer's current choice, kept checked when still offered.
  loadShipping() {
    if (!this.hasShippingFrameTarget) return
    // This load supersedes a debounced one still pending (typing then a cart action): firing it
    // later would reload the same quote and undo a choice made in between.
    clearTimeout(this.quoteTimer)
    const postcode = this.postcode()
    const country = this.country()
    this.quotedFor = `${country}|${postcode}`
    const params = new URLSearchParams({ country, postcode, selected: this.optionKey || this.preferredOptionKey || "" })
    this.shippingFrameTarget.src = `${this.shippingUrlValue}?${params}`
  }

  reloadShipping() {
    if (this.kind !== "parcel") return
    this.quotedFor = null
    this.loadShipping()
  }

  // While a payment is in flight the option stays the one the intent was created for: the
  // recipient and the method list sent at confirmation must match it.
  shippingLoaded() {
    if (this.paying) return this.recheckOption()
    this.repriceDuos()
    this.keepChoice()
    const checked = this.shippingFrameTarget.querySelector("input[name='shipping_option']:checked")
    if (checked) {
      this.applyShipping(checked)
    } else {
      this.optionKey = null
      this.shippingCents = null
      this.setPickup(false)
      this.updateTotal()
    }
  }

  // The quote was requested with the choice of that moment: an option picked while it loaded wins,
  // when the new quote still offers it.
  // Before any option is applied, the one asked for (a prefill's, or the one a Back restored) wins
  // over the quote's default too: an earlier quote rendering after it was asked for (the copy's
  // first quote, requested before the restored option was read) must not put « Standard » back.
  keepChoice() {
    const key = this.optionKey || this.preferredOptionKey
    if (!key) return
    const mine = this.shippingFrameTarget.querySelector(`input[name='shipping_option'][value='${CSS.escape(key)}']`)
    if (mine) mine.checked = true
  }

  // A duo's card (card 11) is priced for the delivery country: the frame carries the items'
  // subtotal for it, and each duo line's price (cart block) and the recap's labels follow.
  // « Payer » prices it the same way on the server (Shop::Checkout#duo_prices_for).
  repriceDuos() {
    const holder = this.shippingFrameTarget.querySelector("[data-payable-subtotal-cents]")
    if (!holder) return
    const subtotal = parseInt(holder.dataset.payableSubtotalCents, 10)
    if (!isNaN(subtotal)) this.subtotalCents = subtotal
    // The recap's items and discount count the subscription lines too (data on the cart block).
    const items = this.element.querySelector("#shop_recap [data-recap-items]")
    if (items) items.textContent = this.formatCents(parseInt(holder.dataset.itemsCents, 10) + (parseInt(this.cartTarget.dataset.subscriptionsGrossCents, 10) || 0))
    const discount = this.element.querySelector("#shop_recap [data-recap-discount]")
    const discountCents = parseInt(holder.dataset.discountCents, 10) + (parseInt(this.cartTarget.dataset.subscriptionsDiscountCents, 10) || 0)
    if (discount) discount.textContent = discountCents > 0 ? `−${this.formatCents(discountCents)}` : "—"
    holder.querySelectorAll("[data-duo-line]").forEach((price) => {
      const row = this.element.querySelector(`#shop_cart [data-duo-line="${price.dataset.duoLine}"]`)
      if (row) row.textContent = price.dataset.label
    })
  }

  selectShipping(event) {
    if (this.paying) return this.recheckOption()
    this.applyShipping(event.target)
  }

  recheckOption() {
    this.shippingFrameTarget.querySelectorAll("input[name='shipping_option']").forEach((input) => {
      input.checked = input.value === this.optionKey
    })
    this.markOptions()
  }

  applyShipping(input) {
    this.optionKey = input.value
    this.rememberChoice({ option: input.value })
    this.preferredOptionKey = null
    this.shippingCents = parseInt(input.dataset.priceCents, 10)
    const label = input.closest(".opt").querySelector(".ot")
    this.optionLabel = label ? label.textContent.trim() : null
    this.markOptions()
    this.setPickup(input.dataset.pickup === "true")
    this.updateTotal()
  }

  markOptions() {
    this.shippingFrameTarget.querySelectorAll(".opt").forEach((opt) => {
      const input = opt.querySelector("input")
      opt.classList.toggle("sel", !!(input && input.checked))
    })
  }

  // Pickup (demo): the address block only asks who comes, by name and phone; Klarna then asks the
  // buyer's billing address (syncKlarnaAddress). Back to a delivery, the typed address is quoted
  // again.
  setPickup(pickup) {
    if (pickup === this.pickup) return
    this.pickup = pickup
    if (this.hasAddressTarget) {
      this.addressTarget.classList.toggle("pickup", pickup)
      this.paintAddressTitle()
      this.paintAddressSub()
      this.phoneLabelTarget.textContent = pickup ? this.phoneLabelTarget.dataset.pickup : this.phoneLabelTarget.dataset.delivery
    }
    // A country changed while the pickup was still checked sent its list without Bancontact; the
    // re-quote that drops the pickup (Belgium) must give it back.
    if (this.elements) this.elements.update({ paymentMethodTypes: this.methodTypes() })
    this.syncKlarnaAddress()
    if (this.hasShippingLabelTarget && !pickup) this.shippingLabelTarget.textContent = this.shippingLabelTarget.dataset.default || this.shippingLabelTarget.textContent
  }

  shippingLabelTargetConnected(element) {
    element.dataset.default = element.textContent
  }

  // ---- Address ----------------------------------------------------------------------------------

  // What the address is for (« votre Coffret et de vos accessoires »): carried by the re-rendered
  // cart block, so it follows the cart.
  // The delivery step's title and line follow the cart (a second Coffret, a card added): data on
  // the cart block (ShopHelper#shop_delivery_heading).
  paintDeliveryScope() {
    if (!this.hasDeliveryTitleTarget || !this.hasCartTarget) return
    const data = this.cartTarget.dataset
    if (data.deliveryTitle) this.deliveryTitleTarget.textContent = data.deliveryTitle
    if (this.hasDeliverySubTarget) {
      this.deliverySubTarget.textContent = data.deliverySub || ""
      this.deliverySubTarget.hidden = !data.deliverySub
    }
  }

  paintAddressSub() {
    if (!this.hasAddressSubTarget || !this.hasCartTarget) return
    const text = this.pickup ? this.cartTarget.dataset.addressSubPickup : this.cartTarget.dataset.addressSub
    if (text) this.addressSubTarget.textContent = text
  }

  giftChanged(event) {
    this.setGift(event.currentTarget.dataset.gift === "1")
    this.writeDraft()
  }

  setGift(gift) {
    if (!this.hasGiftToggleTarget) return
    this.gift = gift
    this.giftToggleTarget.querySelectorAll("button").forEach((button) => button.classList.toggle("on", button.dataset.gift === (gift ? "1" : "0")))
    this.giftLineTarget.hidden = !gift
    this.paintAddressTitle()
    this.rememberChoice({ gift })
  }

  paintAddressTitle() {
    const title = this.addressTitleTarget
    title.textContent = this.pickup ? title.dataset.pickup : (this.gift ? title.dataset.gift : title.dataset.self)
  }

  // Google Places writes « N/A » where it has no postcode or city: never send that as an address.
  addressChanged(event) {
    const field = event && event.target
    if (field && (field === this.postcodeTarget || field === this.cityTarget) && field.value.trim() === "N/A") field.value = ""
    if (field && field.classList) this.clearFieldError(field)
    this.clearError()
    const key = `${this.country()}|${this.postcode()}`
    if (key !== this.quotedFor) {
      clearTimeout(this.quoteTimer)
      this.quoteTimer = setTimeout(() => this.loadShipping(), 300)
    }
    this.refreshPayability()
  }

  emailChanged(event) {
    this.clearFieldError(event.target)
    this.clearError()
    this.refreshPayability()
  }

  postcode() {
    return this.hasPostcodeTarget ? this.postcodeTarget.value.trim() : ""
  }

  // A field left empty is flagged at once (Samuel's mock-up, 2026-10-21), once the buyer has typed
  // elsewhere in the address; « Payer » flags every missing one with the same line.
  fieldLeft(event) {
    const field = event.target
    if (this.kind !== "parcel" || field.value.trim() !== "" || !this.addressMissing().includes(field)) return
    if (this.addressMissing().length === this.requiredAddressFields().length) return
    this.markFieldError(field, this.requiredErrorValue)
  }

  markFieldError(field, message) {
    field.classList.add("field-err")
    field.setAttribute("aria-invalid", "true")
    const anchor = field.closest(".grid2") || field
    let line = anchor.nextElementSibling
    if (!(line && line.classList.contains("errmsg"))) {
      line = document.createElement("div")
      line.className = "errmsg"
      line.id = `err-${field.id}`
      line.setAttribute("role", "alert")
      anchor.insertAdjacentElement("afterend", line)
    }
    // A line left by another check (the Le Pot e-mail's) says the message of this one.
    line.textContent = message
    field.setAttribute("aria-describedby", line.id)
  }

  clearFieldError(field) {
    field.classList.remove("field-err")
    field.removeAttribute("aria-invalid")
    field.removeAttribute("aria-describedby")
    const anchor = field.closest(".grid2") || field
    const line = anchor.nextElementSibling
    if (line && line.classList.contains("errmsg") && !anchor.querySelector(".field-err") && !anchor.classList.contains("field-err")) line.remove()
  }

  requiredAddressFields() {
    const fields = []
    if (this.kind === "parcel") {
      [this.firstNameTarget, this.lastNameTarget].forEach((f) => fields.push(f))
      if (!this.pickup) {
        fields.push(this.streetTarget)
        if (!this.postcodeOptional()) fields.push(this.postcodeTarget)
        fields.push(this.cityTarget)
      }
      fields.push(this.phoneTarget)
    }
    return fields
  }

  addressMissing() {
    return this.requiredAddressFields().filter((field) => field.value.trim() === "")
  }

  // The greyed payment step under an incomplete address: what is missing (Samuel's mock-up).
  paintPayWait() {
    if (!this.hasPayWaitTarget) return
    const missing = this.addressMissing()
    this.payWaitTarget.hidden = missing.length === 0
    if (missing.length === 0) return

    const data = this.payWaitTarget.dataset
    const labels = missing.map((field) => field.dataset.missing)
    const list = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} ${data.and} ${labels[labels.length - 1]}` : labels[0]
    this.payWaitTextTarget.textContent = missing.length === this.requiredAddressFields().length ? data.all : data.some.replace("%{list}", list)
  }

  // The required fields that are still empty: name and phone always, the street, postcode and city
  // for a delivery (the postcode where the country has one: data-postcode-optional on the option).
  missingFields() {
    const fields = []
    if (this.kind === "parcel") {
      [this.firstNameTarget, this.lastNameTarget, this.phoneTarget].forEach((f) => fields.push(f))
      if (!this.pickup) {
        fields.push(this.streetTarget, this.cityTarget)
        if (!this.postcodeOptional()) fields.push(this.postcodeTarget)
      }
    }
    fields.push(this.emailTarget)
    return fields.filter((field) => field.value.trim() === "" || (field === this.emailTarget && !this.validEmail()))
  }

  postcodeOptional() {
    const option = this.hasShipCountryTarget ? this.shipCountryTarget.selectedOptions[0] : null
    return !!(option && option.dataset.postcodeOptional === "true")
  }

  validEmail() {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.emailTarget.value.trim())
  }

  // The payment block appears once the address (or the pickup contact) is filled in (demo's autoReveal).
  addressComplete() {
    if (this.kind !== "parcel") return true
    return this.missingFields().filter((field) => field !== this.emailTarget).length === 0
  }

  // ---- Payability -------------------------------------------------------------------------------

  ready() {
    return this.optionKey !== null && this.missingFields().length === 0
  }

  refreshPayability() {
    if (!this.started || this.kind === "empty") return
    if (this.hasPaymentSectionTarget) this.reveal(this.paymentSectionTarget, this.addressComplete())
    this.paintPayWait()
    const disabled = this.paying
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
    if (this.hasMobileCtaTarget) this.mobileCtaTarget.disabled = disabled
    if (this.hasLepotTarget) this.lepotTarget.toggleAttribute("aria-disabled", disabled)
    // Wallets confirm without our « Payer » check: shown only once everything is filled in.
    const express = this.ready() && !this.paying && this.expressAvailable
    if (this.hasExpressTarget) this.expressTarget.hidden = !express
    if (this.hasExpressOrTarget) this.expressOrTarget.hidden = !express
    // The cart and the recap's promo code are inert during a payment: an edit would reopen the cart
    // and cancel the intent being confirmed. `inert` stops the keyboard too, not only the pointer.
    if (this.hasCartTarget) {
      this.cartTarget.classList.toggle("paying", this.paying)
      this.cartTarget.inert = this.paying
    }
    const recap = document.getElementById("shop_recap")
    if (recap) {
      recap.classList.toggle("paying", this.paying)
      recap.querySelectorAll(".promo, .promo-remove-form").forEach((element) => { element.inert = this.paying })
    }
  }

  // A locked section is only transparent: `inert` keeps Tab out of its fields too.
  reveal(section, on) {
    section.classList.toggle("locked", !on)
    section.classList.toggle("reveal", on)
    section.inert = !on
  }

  // A click with something missing marks it (demo's validateForm) instead of a disabled button.
  // `emailError` replaces the generic line when the e-mail is all that is missing, left empty (Le Pot);
  // a malformed one keeps the field's own « Vérifiez l'adresse email. ».
  checkForm(emailError = null) {
    if (this.optionKey === null) {
      this.fail(this.missingErrorValue)
      if (this.hasDeliveryTarget) this.deliveryTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      return false
    }
    const missing = this.missingFields()
    if (missing.length === 0) return true

    // The error line sits in the payment section, inert while the address is incomplete: screen
    // readers learn it from the focused field instead.
    missing.forEach((field) => {
      const invalid = field === this.emailTarget && field.value.trim() !== ""
      this.markFieldError(field, invalid ? this.emailInvalidErrorValue : this.requiredErrorValue)
    })
    this.reveal(this.paymentSectionTarget, this.addressComplete())
    const emailOnly = missing.length === 1 && missing[0] === this.emailTarget && this.emailTarget.value.trim() === ""
    this.fail(emailError && emailOnly ? emailError : this.missingErrorValue)
    this.bringField(missing[0])
    return false
  }

  // A field to fix, brought to the middle of the screen (clear of the sticky bar). Focused on a
  // desktop; on a phone a focus opens the keyboard, which then covers the field and its message
  // (Samuel's QA, item 51): there it is only scrolled to, and the buyer taps it.
  bringField(field) {
    if (!window.matchMedia(PHONE_LAYOUT).matches) field.focus({ preventScroll: true })
    field.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // ---- Payment ----------------------------------------------------------------------------------

  // The total the page shows and « Payer » names: the hold (or nothing, in setup mode) plus the
  // subscriptions' first charges, each charged on its own after the card is saved.
  displayTotalCents() {
    return this.totalCents() + this.subscriptionCents
  }

  totalCents() {
    if (this.setup || this.kind === "empty") return 0
    // Never under Stripe's minimum charge: a promo code that takes the items under it with a free
    // delivery leaves that much on the items at « Payer » (Shop::PromoDiscount), so the page shows it.
    return Math.max(this.subtotalCents + (this.shippingCents || 0), this.minChargeCentsValue)
  }

  // Klarna's 100 € follows the total: the Payment Element's methods with it. A method the new list
  // drops while it is the one picked (Klarna under 100 €) is forgotten here, with Klarna's billing
  // block, whether or not the Payment Element reports the change (Marcelo, #2334): left mounted, the
  // empty block would make elements.submit() ask a card buyer for a billing address.
  updateTotal() {
    if (this.elements && !this.setup) {
      const types = this.methodTypes()
      this.elements.update({ amount: this.totalCents(), paymentMethodTypes: types })
      if (this.shownPaymentType && types.indexOf(this.shownPaymentType) === -1) {
        this.shownPaymentType = null
        this.syncKlarnaAddress()
      }
    }
    this.paintRecap()
  }

  // expressEvent: the Express Checkout confirm event (Apple Pay, Google Pay, Link), told when the
  // payment fails so its sheet closes instead of spinning until it times out.
  async pay(expressEvent) {
    this.expressEvent = expressEvent && expressEvent.paymentFailed ? expressEvent : null
    if (this.paying || this.kind === "empty") return this.failExpress()
    if (!this.checkForm()) return this.failExpress()
    // A wallet pays without Klarna's billing address: elements.submit() would ask it, and Stripe
    // would attach it to the wallet's payment. If the wallet fails, failExpress shows the block
    // again, empty.
    this.syncKlarnaAddress()
    if (this.klarnaAddressMissing()) {
      console.error("Klarna picked without its billing address block: payment refused")
      return this.fail(this.genericErrorValue)
    }

    if (!this.elements) return this.fail(this.genericErrorValue)

    this.paying = true
    this.hidePayNotice()
    // The total on the button when clicked: what the server must find again (freeze_cart).
    const expectedTotal = this.totalCents()
    this.refreshPayability()
    this.clearError()
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) return this.fail(submitError.message)

      const recipient = this.recipient()
      const response = await fetch(this.paymentUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          address: this.addressPayload(recipient),
          option_key: this.optionKey,
          expected_total_cents: expectedTotal,
          // For the promo code's per-customer limit (a guest has only this e-mail).
          email: this.payerEmail(expressEvent),
          gift: this.giftDelivery()
        })
      })
      const body = await this.answer(response)
      if (body === null) return

      const confirm = body.mode === "setup" ? this.stripe.confirmSetup : this.stripe.confirmPayment
      const { error } = await confirm.call(this.stripe, {
        elements: this.elements,
        clientSecret: body.client_secret,
        confirmParams: this.confirmParams(body.return_url, recipient, expressEvent)
      })
      // Only reached when Stripe did not redirect (declined card, closed popup...).
      if (error) this.fail(error.message)
    } catch (err) {
      this.fail(this.genericErrorValue)
    }
  }

  // The e-mail typed, else the one a wallet sheet gave (Apple Pay, Google Pay, Link).
  payerEmail(expressEvent) {
    const typed = this.emailTarget.value.trim()
    if (typed) return typed
    if (expressEvent && expressEvent.billingDetails && expressEvent.billingDetails.email) return expressEvent.billingDetails.email
    return ""
  }

  // Le Pot Commun (coffrets and gift cards): the server opens a Le Pot order and the buyer pays on
  // Le Pot's page, which sends them back to the confirmation page. Le Pot gives us no e-mail: the one
  // typed (or the signed-in account's, server side) is required.
  async payWithLepot(event) {
    event.preventDefault()
    if (this.paying || this.kind === "empty") return
    if (this.lepotEmailMissing()) return
    if (!this.checkForm()) return

    this.paying = true
    this.hidePayNotice()
    this.refreshPayability()
    this.clearError()
    try {
      const response = await fetch(this.lepotPaymentUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
        body: JSON.stringify({
          address: this.addressPayload(this.recipient()),
          email: this.emailTarget.value.trim(),
          option_key: this.optionKey,
          expected_total_cents: this.totalCents(),
          gift: this.giftDelivery()
        })
      })
      const body = await this.answer(response)
      if (body === null) return

      window.location.href = body.redirect_url
    } catch (err) {
      this.fail(this.genericErrorValue)
    }
  }

  // Everything filled in but the e-mail: Le Pot gives us none, so the page says why it is needed
  // (card F step 30, as the server's email_missing) instead of the generic « champs en rouge ».
  lepotEmailMissing() {
    if (this.optionKey === null || this.emailTarget.value.trim() !== "") return false
    const missing = this.missingFields()
    if (!(missing.length === 1 && missing[0] === this.emailTarget)) return false

    this.clearFieldError(this.emailTarget)
    this.markFieldError(this.emailTarget, this.lepotEmailErrorValue)
    this.fail(this.lepotEmailErrorValue)
    this.bringField(this.emailTarget)
    return true
  }

  // The server's answer to a payment click, or null once the refusal is handled (redirect, reload,
  // or the error shown).
  async answer(response) {
    // Rack::Attack answers 429 in plain text.
    if (response.status === 429) {
      this.fail(this.throttledErrorValue)
      return null
    }
    const body = await response.json()
    if (response.ok) return body

    if (body.redirect_url) {
      window.location.href = body.redirect_url
      return null
    }
    // The lines changed (another tab or browser, a price update): the cart on this page is stale.
    // Nothing was charged (no PaymentIntent is made for a moved total). The page loads again with
    // the new cart, keeps what the buyer typed (the draft) and says why (showCartChanged).
    if (body.subtotal_cents && body.subtotal_cents !== this.subtotalCents) {
      this.failExpress()
      this.writeDraft()
      try { window.sessionStorage.setItem(CART_CHANGED_KEY + this.draftKeyValue, "1") } catch (err) { /* storage unavailable */ }
      window.location.reload()
      return null
    }
    // A moved total or a refused carrier: the options on screen are stale either way.
    this.reloadShipping()
    this.fail(body.error || this.genericErrorValue)
    return null
  }

  // Our fields replaced Stripe's Address Element, so the page sends the shipping address itself (as
  // the pickup always did): the shop's address and the person who comes for a pickup. Gift cards
  // only ship nothing and carry no shipping.
  confirmParams(returnUrl, recipient, expressEvent) {
    const params = { return_url: returnUrl, payment_method_data: { billing_details: this.billingDetails(recipient, expressEvent) } }
    if (recipient) params.shipping = { name: recipient.name, phone: recipient.phone, address: this.stripeAddress(recipient.address) }
    return params
  }

  // « C'est un cadeau » on a delivery (Shop::Journey): the invoice never names the recipient as the
  // buyer. Not on a pickup, which asks who comes, nor for gift cards only.
  giftDelivery() {
    return this.kind === "parcel" && !this.pickup && this.gift
  }

  recipient() {
    if (this.kind !== "parcel") return null

    const name = `${this.firstNameTarget.value.trim()} ${this.lastNameTarget.value.trim()}`.trim()
    const phone = this.internationalPhone(this.phoneTarget.value)
    if (this.pickup) return { name, phone, address: this.pickupAddressValue }

    return {
      name,
      phone,
      address: {
        line1: this.streetTarget.value.trim(),
        line2: this.street2Target.value.trim(),
        city: this.cityTarget.value.trim(),
        postal_code: this.postcode(),
        country: this.country()
      }
    }
  }

  // The Address Element gave phones in E.164: a number typed nationally gets the country's dialling
  // code, its trunk 0 dropped (Italy keeps it: it is part of the number there).
  internationalPhone(raw) {
    const phone = raw.trim().replace(/[\s.\-()]/g, "")
    if (phone === "" || phone.startsWith("+")) return phone
    if (phone.startsWith("00")) return `+${phone.slice(2)}`
    const option = this.hasShipCountryTarget ? this.shipCountryTarget.selectedOptions[0] : null
    const dial = this.pickup ? "33" : (option ? option.dataset.dial : "")
    if (!dial) return phone
    return `+${dial}${dial === "39" ? phone : phone.replace(/^0/, "")}`
  }

  stripeAddress(addr) {
    return { line1: addr.line1, line2: addr.line2 || "", city: addr.city, postal_code: addr.postal_code || "", state: addr.state || "", country: addr.country }
  }

  billingDetails(recipient, expressEvent) {
    const details = {}
    const email = this.payerEmail(expressEvent)
    if (email) details.email = email
    if (!recipient) return details

    // Klarna on a pickup: one identity on the charge, the buyer's from the Address Element (an
    // explicit phone would be merged with it, seen in test mode), never who comes for the parcel.
    if (this.billingAddressElement) return details

    // Klarna needs the full billing address (country alone is refused); the shipping one stands in.
    details.name = recipient.name
    details.address = this.stripeAddress(recipient.address)
    if (recipient.phone) details.phone = recipient.phone
    return details
  }

  addressPayload(recipient) {
    if (!recipient) return {}
    const addr = recipient.address
    return {
      name: recipient.name,
      phone: recipient.phone || "",
      line1: addr.line1,
      line2: addr.line2 || "",
      city: addr.city,
      postal_code: addr.postal_code || "",
      country: addr.country
    }
  }

  // « Annuler » in a gift card's « Modifier » (card F step 18): the panel closes and its fields go
  // back to what they showed when it was rendered (what is saved, or a refused edit shown again).
  // A hidden field Google's autocomplete wrote keeps that value through reset() (a hidden input's
  // value is its default): it goes back to what was rendered (data-rendered).
  cancelRecipient(event) {
    const panel = event.target.closest("details")
    const form = event.target.closest("form")
    if (form) {
      form.reset()
      form.querySelectorAll("input[type='hidden'][data-rendered]").forEach((input) => { input.value = input.dataset.rendered })
    }
    if (panel) panel.open = false
  }

  // ---- Prefill after a refused payment, or the buyer's draft -------------------------------------

  hasPrefill() {
    return Object.keys(this.prefill).length > 0
  }

  // What the buyer typed, kept for this tab (sessionStorage): a reload of the cart (a line added or
  // removed, back from Le Pot Commun, its return link) fills the form again (card F step 5).
  // Storage can be unavailable (private mode, blocked): the form then simply starts empty.
  writeDraft() {
    if (this.kind === "empty") return
    const draft = { email: this.emailTarget.value.trim() }
    if (this.kind === "parcel") {
      Object.assign(draft, {
        gift: this.giftDelivery(),
        first_name: this.firstNameTarget.value.trim(),
        last_name: this.lastNameTarget.value.trim(),
        phone: this.phoneTarget.value.trim(),
        address: { country: this.country(), line1: this.streetTarget.value.trim(), line2: this.street2Target.value.trim(), postal_code: this.postcodeTarget.value.trim(), city: this.cityTarget.value.trim() }
      })
    }
    try { window.sessionStorage.setItem(DRAFT_KEY + this.draftKeyValue, JSON.stringify(draft)) } catch (err) { /* storage unavailable */ }
  }

  // Once, after the reload a stale cart caused (answer): the notice above the cart, in view.
  showCartChanged() {
    let changed = false
    try {
      changed = window.sessionStorage.getItem(CART_CHANGED_KEY + this.draftKeyValue) === "1"
      window.sessionStorage.removeItem(CART_CHANGED_KEY + this.draftKeyValue)
    } catch (err) { /* storage unavailable */ }
    if (!changed || !this.hasCartChangedTarget) return
    this.cartChangedTarget.hidden = false
    this.cartChangedTarget.scrollIntoView({ block: "center" })
    // Shown with its text already in it: a live region alone may not be read, the focus is.
    this.cartChangedTarget.focus({ preventScroll: true })
  }

  readDraft() {
    try { return JSON.parse(window.sessionStorage.getItem(DRAFT_KEY + this.draftKeyValue)) || {} } catch (err) { return {} }
  }

  applyPrefill(prefill = this.prefill) {
    if (prefill.email) this.emailTarget.value = prefill.email
    if (this.kind !== "parcel") return
    if (prefill.gift) this.setGift(true)

    if (prefill.first_name || prefill.last_name) {
      this.firstNameTarget.value = prefill.first_name || ""
      this.lastNameTarget.value = prefill.last_name || ""
    } else if (prefill.name) {
      const parts = prefill.name.trim().split(/\s+/)
      this.firstNameTarget.value = parts.shift() || ""
      this.lastNameTarget.value = parts.join(" ")
    }
    if (prefill.phone) this.phoneTarget.value = prefill.phone
    const addr = prefill.address
    // Not for a pickup (its address is the shop's), nor in a country no longer served.
    if (prefill.pickup || !addr) return
    const served = Array.from(this.shipCountryTarget.options).some((option) => option.value === addr.country)
    if (!served) return

    this.shipCountryTarget.value = addr.country
    this.addressFormTarget.setAttribute("data-address-form-locked-country-code-value", addr.country)
    this.streetTarget.value = addr.line1 || ""
    this.street2Target.value = addr.line2 || ""
    this.postcodeTarget.value = addr.postal_code || ""
    this.cityTarget.value = addr.city || ""
    // Elements were built before the country was set (setUpStripe runs first): a Belgian
    // delivery gets its Bancontact back, as a country changed by hand does.
    if (this.elements) this.elements.update({ paymentMethodTypes: this.methodTypes() })
  }

  // ---- Errors and helpers -----------------------------------------------------------------------

  failExpress() {
    const event = this.expressEvent
    if (!event) return
    this.expressEvent = null
    this.syncKlarnaAddress()
    event.paymentFailed({ reason: "fail" })
  }

  fail(message) {
    // Whatever the wallet or Stripe.js throws, the page comes out of the payment.
    try {
      this.failExpress()
    } catch (err) {
      console.error(err)
    }
    this.paying = false
    if (this.hasErrorsTarget) this.errorsTarget.textContent = message
    // Under the Payment Element (and Link's block), out of view on a phone: brought to the middle of
    // the screen, clear of the sticky bar, and focused so it is read. A field checkForm marks
    // missing takes the scroll and the focus back right after.
    if (message && this.hasErrorsTarget) {
      this.errorsTarget.scrollIntoView({ block: "center" })
      this.errorsTarget.focus({ preventScroll: true })
    }
    this.refreshPayability()
    if (this.cartChangedWhilePaying) {
      this.cartChangedWhilePaying = false
      this.syncCart()
    }
  }

  // Stripe also sends the Element's first state (empty, on the card tab) late on a slow load,
  // after a click may have shown an error: only the buyer's own changes clear it.
  paymentChanged(event) {
    const type = event.value ? event.value.type : null
    if (!event.empty || (this.shownPaymentType && type !== this.shownPaymentType)) this.clearError()
    this.shownPaymentType = type
    this.syncKlarnaAddress()
  }

  clearError() {
    if (this.hasErrorsTarget && this.errorsTarget.textContent !== "") this.errorsTarget.textContent = ""
  }

  // A refused payment's notice above « Payer » goes with the next attempt (card F step 14), not
  // with clearError, which the Payment Element's first change event also calls on load.
  hidePayNotice() {
    this.element.querySelectorAll(".pay-notice").forEach((notice) => { notice.hidden = true })
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  // As ShopHelper#shop_price: whole euros without decimals (« 75 € »), else two (« 20,90 € »).
  formatCents(cents) {
    const digits = cents % 100 === 0 ? 0 : 2
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(cents / 100)
  }
}
